/**
 * Main Layout — Top Navigation Bar.
 *
 * Horizontal navigation with dropdown menus replacing the vertical sidebar.
 * The nav bar contains: logo, page group dropdowns, and right-side actions
 * (search, calculator, notifications, theme, user menu).
 *
 * Page groups:
 * - Dashboard (direct link)
 * - Inventory (dropdown: Assets, Yard Map)
 * - Procurement (dropdown: Purchase Orders, Vendors, Credit Accounts)
 * - Maintenance (dropdown: Work Orders, Recurring, Scan)
 * - Sales (dropdown: Orders, Pipeline, Quotes, Customers, Auctions)
 * - Collaborate (dropdown: Messages, Planner)
 * - Reports (dropdown: Analytics, Insights, Benchmarks, What-If)
 * - Admin (gear icon → dropdown)
 */

import { useState, useRef, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  LayoutDashboard,
  Package,
  MapPin,
  FileText,
  Building2,
  CreditCard,
  Wrench,
  RefreshCw,
  ScanLine,
  ShoppingCart,
  Target,
  Receipt,
  UserCircle,
  Gavel,
  BarChart3,
  Lightbulb,
  Trophy,
  FlaskConical,
  MessageSquare,
  CalendarDays,
  Settings2,
  Users,
  ClipboardList,
  Globe,
  Bell,
  Search,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Calculator,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useLogout } from "@/features/auth/api";
import { useThemeStore } from "@/stores/themeStore";
import Badge from "@/components/ui/Badge";
import AppLogo from "@/components/ui/AppLogo";
import MobileNav from "@/components/ui/MobileNav";
import CommandPalette from "@/components/ui/CommandPalette";
import ResellerCalculator from "@/components/ui/ResellerCalculator";
import ScanModal from "@/components/ui/ScanModal";
import ToastContainer from "@/components/ui/ToastContainer";
import { useCalculatorStore } from "@/stores/calculatorStore";

// ---------------------------------------------------------------------------
// Navigation Structure
// ---------------------------------------------------------------------------

interface NavLink {
  label: string;
  path: string;
  icon: React.ElementType;
  description?: string;
  /** Hides this link alone. The group-level `permission` hides the whole menu. */
  permission?: string;
  /**
   * Shows the link if the user holds **any** of these. For a hub page that
   * covers several unrelated things — Workflows is statuses, forms and
   * automations; Finance is tax schemes and exchange rates — one permission
   * cannot describe it, and demanding all of them would hide the page from
   * somebody who has a reason to be on it.
   */
  anyPermission?: string[];
}

interface NavGroup {
  label: string;
  icon: React.ElementType;
  path?: string; // Direct link (no dropdown)
  links?: NavLink[]; // Dropdown items
  permission?: string;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/",
  },
  {
    label: "Inventory",
    icon: Package,
    permission: "inventory.view",
    links: [
      {
        label: "Assets",
        path: "/inventory",
        icon: Package,
        description: "Browse and manage inventory",
      },
      {
        label: "Facility Map",
        path: "/inventory?tab=yard-map",
        icon: MapPin,
        description: "Visual lot placement",
      },
    ],
  },
  {
    label: "Procurement",
    icon: FileText,
    permission: "purchase_orders.view",
    links: [
      {
        label: "Purchase Orders",
        path: "/procurement",
        icon: FileText,
        description: "Manage procurement",
      },
      {
        label: "Vendors",
        path: "/procurement?tab=vendors",
        icon: Building2,
        description: "Vendor contacts and terms",
      },
      {
        label: "Credit Accounts",
        path: "/procurement?tab=credit",
        icon: CreditCard,
        permission: "credit.view",
        description: "Lines of credit and balances",
      },
    ],
  },
  {
    label: "Maintenance",
    icon: Wrench,
    permission: "work_orders.view",
    links: [
      {
        label: "Work Orders",
        path: "/shop",
        icon: Wrench,
        description: "Active and completed work",
      },
      {
        label: "Recurring",
        path: "/shop?tab=recurring",
        icon: RefreshCw,
        description: "Scheduled maintenance templates",
      },
    ],
  },
  {
    label: "Sales",
    icon: ShoppingCart,
    permission: "sales.view",
    links: [
      {
        label: "Orders",
        path: "/sales",
        icon: ShoppingCart,
        description: "Sales orders and invoices",
      },
      {
        label: "Pipeline",
        path: "/sales?tab=pipeline",
        icon: Target,
        description: "Deal tracking and forecasting",
      },
      {
        label: "Quotes",
        path: "/sales?tab=quotes",
        icon: Receipt,
        description: "Proposals and negotiations",
      },
      {
        label: "Customers",
        path: "/sales?tab=customers",
        icon: UserCircle,
        description: "Customer management and CRM",
      },
      {
        label: "Auctions",
        path: "/sales?tab=auctions",
        icon: Gavel,
        description: "Live auction events",
      },
    ],
  },
  {
    label: "Collaborate",
    icon: MessageSquare,
    links: [
      {
        label: "Messages",
        path: "/messages",
        icon: MessageSquare,
        description: "Team chat and channels",
      },
      {
        label: "Planner",
        path: "/planner",
        icon: CalendarDays,
        description: "Calendar and task planning",
      },
    ],
  },
  {
    label: "Reports",
    icon: BarChart3,
    permission: "analytics.view",
    links: [
      {
        label: "Analytics",
        path: "/reports",
        icon: BarChart3,
        description: "Dashboards and metrics",
      },
      {
        label: "Insights",
        path: "/reports?tab=insights",
        icon: Lightbulb,
        description: "AI-driven recommendations",
      },
      {
        label: "Benchmarks",
        path: "/reports?tab=benchmarks",
        icon: Trophy,
        description: "Industry comparisons",
      },
      {
        label: "What-If",
        path: "/reports?tab=what-if",
        icon: FlaskConical,
        description: "Scenario modeling",
      },
    ],
  },
];

// The gear menu is gated on `admin.access` and each entry on what is behind
// it (FS-007b). Before the split, the menu and all five entries rendered on
// `settings.view`, so granting somebody lifecycle statuses meant granting them
// the organization's settings screens as well — the two could not be
// separated. The permissions below are the ones the APIs behind each hub
// actually require.
const ADMIN_LINKS: NavLink[] = [
  {
    label: "Settings",
    path: "/admin/settings",
    icon: Settings2,
    description: "Organization configuration",
    permission: "settings.view",
  },
  {
    label: "Team & Access",
    path: "/admin/team",
    icon: Users,
    description: "Users and permissions",
    permission: "users.view",
  },
  {
    label: "Workflows",
    path: "/admin/workflows",
    icon: ClipboardList,
    description: "Processes, forms, automations",
    anyPermission: ["statuses.manage", "forms.create", "automations.manage"],
  },
  {
    label: "Finance",
    path: "/admin/finance",
    icon: CreditCard,
    description: "Tax schemes and exchange rates",
    // Tax schemes are gated on `settings.manage`, exchange rates on
    // `credit.view` — two different things sharing a page.
    anyPermission: ["settings.manage", "credit.view"],
  },
  {
    label: "Integrations",
    path: "/admin/integrations",
    icon: Globe,
    description: "Third-party connections",
    permission: "integrations.manage",
  },
];

function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { user } = useAuth();
  const logout = useLogout();
  const { isDark, toggle: toggleTheme } = useThemeStore();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const { isOpen: calcOpen, toggleCalc, closeCalc } = useCalculatorStore();

  const userMenuRef = useRef<HTMLDivElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filter nav groups by permission, then their links. A link can carry its
  // own permission where the group is broader than one of its entries —
  // Credit Accounts sits under Procurement but reads financial data, so it
  // follows `credit.view` rather than `purchase_orders.view` (P0-3).
  const filteredGroups = NAV_GROUPS.filter(
    (g) => !g.permission || hasPermission(g.permission),
  ).map((g) =>
    g.links
      ? {
          ...g,
          links: g.links.filter(
            (l) => !l.permission || hasPermission(l.permission),
          ),
        }
      : g,
  );
  // The gear menu follows `admin.access`, not `settings.view` (FS-007b).
  // `settings.view` was doing two jobs: one route read organization config
  // with it, and this menu — the whole administration hub, including users,
  // roles, statuses, automations and integrations — rendered on it. So a role
  // could not be given one without the other: anybody who was meant to manage
  // lifecycle statuses got the settings screens too, and a role granted
  // `statuses.manage` alone had no menu to reach it from.
  //
  // Every permission under Administration now implies `admin.access`, so this
  // is true for exactly the people who have something behind the menu.
  const adminLinks = ADMIN_LINKS.filter(
    (l) =>
      (!l.permission || hasPermission(l.permission)) &&
      (!l.anyPermission || hasAnyPermission(...l.anyPermission)),
  );
  // `admin.access` alone can leave an empty menu — `org.manage` implies it and
  // has no screen yet — so an empty gear icon never renders.
  const showAdmin = hasPermission("admin.access") && adminLinks.length > 0;

  // Keyboard shortcuts.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "c"
      ) {
        e.preventDefault();
        toggleCalc();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menus on click outside.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      )
        setUserMenuOpen(false);
      if (
        adminMenuRef.current &&
        !adminMenuRef.current.contains(e.target as Node)
      )
        setAdminMenuOpen(false);
      if (navRef.current && !navRef.current.contains(e.target as Node))
        setOpenDropdown(null);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile menu on navigation.
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const initials = user?.full_name
    ? user.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

  const roleBadgeVariant =
    user?.role === "owner" || user?.role === "admin"
      ? "danger"
      : user?.role === "manager"
        ? "warning"
        : "default";

  const isPathActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    const basePath = path.split("?")[0] ?? path;
    return location.pathname.startsWith(basePath);
  };

  const isGroupActive = (group: NavGroup) => {
    if (group.path) return isPathActive(group.path);
    return group.links?.some((l) => isPathActive(l.path)) || false;
  };

  // Hover-based dropdown with delay.
  const handleGroupEnter = (label: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(label);
  };
  const handleGroupLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <div className="flex h-screen flex-col">
      {/* ═══════════════════════════════════════════════════════════════════
          TOP NAVIGATION BAR
          ═══════════════════════════════════════════════════════════════════ */}
      <header
        className="sticky top-0 z-40 w-full bg-[var(--surface-base)]/95 backdrop-blur-xl"
        style={{
          borderBottom: "1px solid transparent",
          borderImage:
            "linear-gradient(to right, transparent, var(--border-default), transparent) 1",
        }}
      >
        <nav className="flex h-14 items-center justify-between px-4 lg:px-6">
          {/* ── Left: Logo + Nav Groups ── */}
          <div className="flex items-center gap-1" ref={navRef}>
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 mr-4 shrink-0 hover:opacity-90 transition-opacity"
            >
              <AppLogo size="sm" />
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-[15px] font-semibold tracking-tight text-[var(--text-primary)]">
                  Trade
                  <span className="font-bold text-[var(--color-interactive-primary)]">
                    Route
                  </span>
                </span>
              </div>
            </Link>

            {/* Desktop Nav Groups */}
            <div className="hidden lg:flex items-center gap-0.5">
              {filteredGroups.map((group) => {
                const active = isGroupActive(group);
                const isOpen = openDropdown === group.label;

                // Direct link (no dropdown).
                if (group.path && !group.links) {
                  return (
                    <Link
                      key={group.label}
                      to={group.path}
                      className={clsx(
                        "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-2 text-[13px] font-medium transition-all duration-150",
                        active
                          ? "text-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]",
                      )}
                    >
                      <group.icon className="h-4 w-4" />
                      {group.label}
                    </Link>
                  );
                }

                // Dropdown group.
                return (
                  <div
                    key={group.label}
                    className="relative"
                    onMouseEnter={() => handleGroupEnter(group.label)}
                    onMouseLeave={handleGroupLeave}
                  >
                    <button
                      onClick={() =>
                        setOpenDropdown(isOpen ? null : group.label)
                      }
                      className={clsx(
                        "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-2 text-[13px] font-medium transition-all duration-150",
                        active || isOpen
                          ? "text-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]",
                      )}
                    >
                      <group.icon className="h-4 w-4" />
                      {group.label}
                      <ChevronDown
                        className={clsx(
                          "h-3 w-3 transition-transform duration-200",
                          isOpen && "rotate-180",
                        )}
                      />
                    </button>

                    {/* Dropdown panel */}
                    {isOpen && group.links && (
                      <div
                        className="absolute left-0 top-full mt-1 w-[280px] rounded-[var(--radius-lg)] elevation-3 py-1.5 animate-scale-in z-50"
                        onMouseEnter={() => handleGroupEnter(group.label)}
                        onMouseLeave={handleGroupLeave}
                      >
                        {group.links.map((link) => (
                          <Link
                            key={link.path}
                            to={link.path}
                            onClick={() => setOpenDropdown(null)}
                            className={clsx(
                              "flex items-start gap-3 px-3 py-2.5 mx-1 rounded-[var(--radius-md)] transition-colors",
                              isPathActive(link.path)
                                ? "bg-[var(--color-interactive-primary)]/8 text-[var(--color-interactive-primary)]"
                                : "text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]",
                            )}
                          >
                            <link.icon className="h-4 w-4 mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
                            <div>
                              <p className="text-sm font-medium">
                                {link.label}
                              </p>
                              {link.description && (
                                <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                                  {link.description}
                                </p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Right: Actions ── */}
          <div className="flex items-center gap-1.5">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex items-center gap-2 h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--color-glass)] px-3 text-xs text-[var(--text-tertiary)] transition-colors hover:bg-[var(--color-glass-hover)] hover:text-[var(--text-secondary)]"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Search...</span>
              <kbd className="ml-1 rounded border border-[var(--border-default)] px-1 py-0.5 text-[10px] font-mono">
                ⌘K
              </kbd>
            </button>

            {/* Scan */}
            <button
              onClick={() => setScanOpen(true)}
              title="Scan barcode or QR code"
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-interactive-primary)]"
            >
              <ScanLine className="h-4 w-4" />
            </button>

            {/* Notifications */}
            <a
              href="/notifications"
              className="relative flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)]"
            >
              <Bell className="h-4 w-4" />
            </a>

            {/* Calculator */}
            <button
              onClick={toggleCalc}
              title="Calculator (⌘⇧C)"
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)]"
            >
              <Calculator className="h-4 w-4" />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? "Light mode" : "Dark mode"}
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)]"
            >
              {isDark ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>

            {/* Admin (gear icon) */}
            {showAdmin && (
              <div className="relative" ref={adminMenuRef}>
                <button
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  title="Administration"
                  className={clsx(
                    "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] transition-colors",
                    adminMenuOpen
                      ? "bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                      : "text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)]",
                  )}
                >
                  <Settings2 className="h-4 w-4" />
                </button>
                {adminMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-[280px] rounded-[var(--radius-lg)] elevation-3 py-1.5 animate-scale-in z-50">
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                      Administration
                    </p>
                    {adminLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setAdminMenuOpen(false)}
                        className="flex items-start gap-3 px-3 py-2.5 mx-1 rounded-[var(--radius-md)] text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)] transition-colors"
                      >
                        <link.icon className="h-4 w-4 mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
                        <div>
                          <p className="text-sm font-medium">{link.label}</p>
                          {link.description && (
                            <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                              {link.description}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="hidden sm:block h-6 w-px bg-[var(--border-subtle)] mx-1" />

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[10px] font-semibold text-white shadow-sm shadow-brand-500/25">
                  {initials}
                </div>
                <span className="hidden lg:block text-sm font-medium text-[var(--text-primary)]">
                  {user?.full_name}
                </span>
                <ChevronDown className="h-3 w-3 text-[var(--text-tertiary)]" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 z-50 mt-1 w-56 rounded-[var(--radius-lg)] elevation-3 py-1 animate-scale-in">
                  <div className="border-b border-[var(--border-subtle)] px-4 py-3">
                    <p className="text-sm font-semibold text-[var(--text-primary)]">
                      {user?.full_name}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      {user?.email}
                    </p>
                    <Badge variant={roleBadgeVariant} className="mt-1.5">
                      {user?.role}
                    </Badge>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--color-surface-hover)]"
                  >
                    Edit profile
                  </Link>
                  <Link
                    to="/preferences"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--color-surface-hover)]"
                  >
                    Preferences
                  </Link>
                  <div className="border-t border-[var(--border-subtle)] my-1" />
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--text-danger)] hover:bg-[var(--fill-danger)] dark:hover:bg-red-900/10"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] lg:hidden"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </nav>

        {/* ── Mobile Menu Overlay ── */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[var(--border-subtle)] bg-[var(--surface-base)] max-h-[calc(100vh-56px)] overflow-y-auto">
            <div className="px-4 py-4 space-y-4">
              {filteredGroups.map((group) => (
                <div key={group.label}>
                  {group.path && !group.links ? (
                    <Link
                      to={group.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={clsx(
                        "flex items-center gap-2 px-3 py-2.5 rounded-[var(--radius-md)] text-sm font-medium",
                        isGroupActive(group)
                          ? "text-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/8"
                          : "text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]",
                      )}
                    >
                      <group.icon className="h-4 w-4" /> {group.label}
                    </Link>
                  ) : (
                    <>
                      <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                        {group.label}
                      </p>
                      {group.links?.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={clsx(
                            "flex items-center gap-2 px-3 py-2 rounded-[var(--radius-md)] text-sm",
                            isPathActive(link.path)
                              ? "text-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/6"
                              : "text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]",
                          )}
                        >
                          <link.icon className="h-4 w-4 text-[var(--text-tertiary)]" />{" "}
                          {link.label}
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              ))}

              {showAdmin && (
                <div className="border-t border-[var(--border-subtle)] pt-3">
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                    Administration
                  </p>
                  {adminLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-md)] text-sm text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]"
                    >
                      <link.icon className="h-4 w-4 text-[var(--text-tertiary)]" />{" "}
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          PAGE CONTENT
          ═══════════════════════════════════════════════════════════════════ */}
      <main className="flex-1 overflow-y-auto bg-[var(--surface-sunken)] p-4 pb-20 lg:p-6 lg:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileNav />

      {/* Global Scan Modal */}
      <ScanModal isOpen={scanOpen} onClose={() => setScanOpen(false)} />

      {/* Command Palette / Global Search */}
      <CommandPalette
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />

      {/* Reseller Calculator */}
      <ResellerCalculator isOpen={calcOpen} onClose={closeCalc} />

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
}

export default MainLayout;
